import { Component, computed, effect, inject, signal } from '@angular/core';

import { GridOptions, GridEmitter, UiGridComponent } from '@program-suite/ui-grid';
import { PolicyFormGridStore } from '../data/policy-form-grid.store';
import { FormViewModel, LookupStore, ProgramEnum } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';

@Component({
  selector: 'lib-feature-policy-forms-grid',
  imports: [UiGridComponent],
  templateUrl: './feature-policy-forms-grid.component.html',
  styleUrl: './feature-policy-forms-grid.component.scss',
})
export class FeaturePolicyFormsGridComponent {
  policyFormGridStore = inject(PolicyFormGridStore);
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  defaultSortColumn = 'policyEffectiveDate';
  defaultSortDirection = 'DESCENDING';
  agencyId = signal<number | null | undefined>(this.authStore.user()?.agencyId ?? undefined);
  data = computed(() => {
    // filter search term
    const filterValues = this.policyFormGridStore.filterValues() as { searchTerm?: string };
    if (filterValues.searchTerm && filterValues.searchTerm !== '') {
      return this.policyFormGridStore.policyForms()?.filter((item) => {
        const searchTerm = filterValues.searchTerm?.toLowerCase() ?? '';
        return (
          item.title?.toLowerCase().includes(searchTerm) ||
          item.formNumber?.toLowerCase().includes(searchTerm) ||
          item.notes?.toLowerCase().includes(searchTerm)
        );
      }) ?? [];
    }
    return this.policyFormGridStore.policyForms() ?? []
  });
  gridOptions = signal<GridOptions>(
    {
      filterData: [
        { name: 'Search', formControlName: 'searchTerm', type: 'search' },
        { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup:  this.lookupStore.programs, defaultValue: '1', hideAllInDropdown: true },
        { name: 'State', formControlName: 'stateId', type: 'dropdown', lookup: this.lookupStore.programStates, defaultValue: 'CA', hideAllInDropdown: true },
      ],
      filterValues: this.policyFormGridStore.filterValues,
      openFilters: true,
      columns: [
        { header: 'Title', controlName: 'title', displayPipe: 'truncate'},
        { header: 'Form Number', controlName: 'formNumber'},
        { header: 'Condition', controlName: 'required'},
        { header: 'Program', controlName: 'program'},
        { header: 'State', controlName: 'state'}, 
        { header: 'Notes', controlName: 'notes'},
        { header: 'Action', controlName: 'action', 
          actionColumnitems: [
            { type: 'icon', buttonAction: 'action',
              conditionalDisplay: { calculateDisplay: () => 'eye', column: 'url' },
             },
          ]
        }
      ],
      data: this.data,
      frontEndPagination: true,
      fetchData: false,
      displaySearch: false,
    }
  );

  constructor() {
    effect(() => {
      const programs = this.lookupStore.programs(); 
      const filterValues = this.policyFormGridStore.filterValues() as { programId?: string };
      const programId = filterValues.programId;
      if (programId && programs) {
        const program = programs.find(p => p.id === programId);
        this.lookupStore.loadProgramStates( program?.name ?? '', this.getDate())
      }
    });
  }


  ngOnInit(): void {
    this.lookupStore.loadLookup('programs');
  }

  getDate() {
    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    return formattedDate;
  }

  fetchAll(rawParams?: any) {
    const { programId, stateId} = rawParams;
    const form = {
      ProgramCode: programId == '' ? undefined : programId,
      state: stateId == '' ? undefined : stateId,
    };
    this.policyFormGridStore.setFilterValues(rawParams);
    this.policyFormGridStore.getPolicyFormGrid(form.ProgramCode,  form.state as string);
  }

  actionEvent(event: GridEmitter) {
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      this.fetchAll(event.data);
    } else if (event.action === 'clearFilters') {
      this.fetchAll(event.data);
    } else if (event.action === 'onDestroy') {
      this.policyFormGridStore.setFilterValues(event.data ?? {});
    } else if (event.action === 'action') {
      const data = event.data as FormViewModel;
      window.open(data?.url ?? '', '_blank');
    }
  }
}